import { ARROW_PATHS } from "./GhxpArrows";
import logoGhExperience from "../../../assets/img/ghExperience/logo-ghexperience.svg";
import logoGh from "../../../assets/img/ghExperience/logo-gh-white.png";
import logoRemus from "../../../assets/img/ghExperience/logo-remus.svg";
import yearBackground from "../../../assets/img/ghExperience/year-2026.svg";

// "Salvar meu convite": desenha o mesmo cartão da tela (sem a contagem "Faltam N dias", que envelhece)
// num PNG 1080x1350 com <canvas>, sem bibliotecas. As medidas seguem o CSS do .ghxpPass em "em".
// Computador: baixa o arquivo direto. Celular: abre o compartilhamento nativo (salvar na galeria, enviar),
// com download como alternativa.

const W = 1080;
const H = 1350;
const CARD_W = 800;
const EM = CARD_W / 23; // .ghxpPass tem 23em de largura
const CARD_H = Math.round(31 * EM); // altura mínima da face: 31em
const ORANGE = "#ff6700";
const WHITE = "#f7f7f7";
const GRAY = "#c0c5cc";
const FONT = "Inter, 'Helvetica Neue', Arial, sans-serif";
const TOUCH = typeof window !== "undefined" && window.matchMedia("(hover: none) and (pointer: coarse)").matches;

export const canSavePass = () => typeof HTMLCanvasElement !== "undefined" && "toBlob" in HTMLCanvasElement.prototype;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`imagem: ${src}`));
    img.src = src;
  });
}

// SVGs só com viewBox não têm tamanho próprio (o Firefox não os desenha no canvas): injeta width/height.
// Se a leitura falhar, tenta a URL direta; se ainda falhar, o desenho segue sem essa imagem.
async function loadSvg(url, width, height) {
  try {
    const text = await (await fetch(url)).text();
    const blobUrl = URL.createObjectURL(new Blob([text.replace(/<svg\b/, `<svg width="${width}" height="${height}"`)], { type: "image/svg+xml" }));
    try {
      return await loadImage(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } catch (error) {
    return loadImage(url).catch(() => null);
  }
}

// Texto em caixa alta com espaçamento entre letras (letterSpacing do canvas ainda não é universal).
function spaced(ctx, text, x, y, spacing) {
  let cursor = x;
  ctx.textAlign = "left";
  [...text].forEach((ch) => {
    ctx.fillText(ch, cursor, y);
    cursor += ctx.measureText(ch).width + spacing;
  });
}

// Quebra por palavras na largura dada (o nome no cartão quebra igual ao da tela).
function wrapWords(ctx, text, maxWidth, spacing) {
  const width = (str) => ctx.measureText(str).width + spacing * Math.max(0, str.length - 1);
  const lines = [];
  let line = "";
  text.split(" ").forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (line && width(test) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return lines;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

async function drawPass({ name, sizeEm, labels }) {
  if (document.fonts) {
    await Promise.all(["400 32px Inter", "500 28px Inter", "600 96px Inter"].map((font) => document.fonts.load(font).catch(() => null)));
  }
  const [logo, pattern, gh, remus] = await Promise.all([
    loadSvg(logoGhExperience, 912, 83),
    loadSvg(yearBackground, 1580, 1366),
    loadImage(logoGh).catch(() => null),
    loadSvg(logoRemus, 237, 63),
  ]);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.textBaseline = "alphabetic";

  // Fundo navy com um brilho quente atrás do cartão.
  ctx.fillStyle = "#0b1628";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, H * 0.46, 40, W / 2, H * 0.46, W * 0.75);
  glow.addColorStop(0, "rgba(255, 140, 60, 0.16)");
  glow.addColorStop(1, "rgba(255, 103, 0, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  const x0 = (W - CARD_W) / 2;
  const y0 = Math.round((H - CARD_H) / 2) - 28;
  const em = (n) => n * EM;

  // Cartão: sombra, face em gradiente, padrão "2026" recortado, borda fina e brilho quente no topo.
  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
  ctx.shadowBlur = 70;
  ctx.shadowOffsetY = 34;
  roundRect(ctx, x0, y0, CARD_W, CARD_H, em(1.2));
  const face = ctx.createLinearGradient(x0, y0, x0 + CARD_W * 0.55, y0 + CARD_H);
  face.addColorStop(0, "#1b2c47");
  face.addColorStop(0.42, "#142135");
  face.addColorStop(1, "#0c1829");
  ctx.fillStyle = face;
  ctx.fill();
  ctx.restore();

  ctx.save();
  roundRect(ctx, x0, y0, CARD_W, CARD_H, em(1.2));
  ctx.clip();
  if (pattern) {
    const pw = CARD_W * 1.6;
    ctx.globalAlpha = 0.45;
    ctx.drawImage(pattern, x0 + CARD_W - pw + CARD_W * 0.4, y0 - CARD_H * 0.06, pw, (pw * 1366) / 1580);
    ctx.globalAlpha = 1;
  }
  const edge = ctx.createLinearGradient(x0, 0, x0 + CARD_W, 0);
  edge.addColorStop(0, "rgba(255, 170, 110, 0.15)");
  edge.addColorStop(0.5, "rgba(255, 170, 110, 0.6)");
  edge.addColorStop(1, "rgba(255, 170, 110, 0.15)");
  ctx.fillStyle = edge;
  ctx.fillRect(x0, y0, CARD_W, 2);
  ctx.restore();

  roundRect(ctx, x0 + 1, y0 + 1, CARD_W - 2, CARD_H - 2, em(1.2));
  ctx.strokeStyle = "rgba(247, 247, 247, 0.1)";
  ctx.lineWidth = 2;
  ctx.stroke();

  const left = x0 + em(1.7);
  const right = x0 + CARD_W - em(1.7);
  const inner = right - left;

  // Cabeçalho: logo e "Convite pessoal".
  let y = y0 + em(1.8);
  if (logo) ctx.drawImage(logo, left, y, (em(1.05) * 912) / 83, em(1.05));
  y += em(1.05) + em(0.7) + em(0.62);
  ctx.fillStyle = GRAY;
  ctx.font = `500 ${em(0.62)}px ${FONT}`;
  spaced(ctx, labels.pass.toUpperCase(), left, y, em(0.62) * 0.22);

  // Convidado(a) e o nome, no mesmo tamanho da tela (2.2 / 1.7 / 1.35em).
  y += em(2) + em(0.68);
  ctx.font = `500 ${em(0.68)}px ${FONT}`;
  spaced(ctx, labels.guest.toUpperCase(), left, y, em(0.68) * 0.22);
  const nameSize = em(sizeEm);
  ctx.fillStyle = WHITE;
  ctx.font = `600 ${nameSize}px ${FONT}`;
  const nameLines = wrapWords(ctx, name.toUpperCase(), inner, nameSize * 0.03).slice(0, 3);
  y += em(0.45);
  nameLines.forEach((line) => {
    y += nameSize * 1.06;
    spaced(ctx, line, left, y - nameSize * 0.18, nameSize * 0.03);
  });

  // Picote e canhoto, presos à base do cartão (como no CSS: 7.2em).
  const tearY = y0 + CARD_H - em(7.2);
  const stubMid = tearY + em(3.6);

  // Campos do evento logo acima do picote.
  const gridBottom = tearY - em(1.5);
  const smallSize = em(0.78);
  const ddSize = em(1);
  const placeTop = gridBottom - smallSize * 1.3 - em(0.3) - ddSize * 1.3 - em(0.35) - em(0.68);
  const rowTop = placeTop - em(1.1) - ddSize * 1.3 - em(0.35) - em(0.68);
  const gridTop = rowTop - em(1.3);
  ctx.fillStyle = "rgba(192, 197, 204, 0.16)";
  ctx.fillRect(left, gridTop, inner, 1.5);
  const colB = left + (inner - em(1)) / 2 + em(1);
  const field = (label, value, x, top) => {
    ctx.fillStyle = GRAY;
    ctx.font = `500 ${em(0.68)}px ${FONT}`;
    spaced(ctx, label.toUpperCase(), x, top + em(0.68) * 0.8, em(0.68) * 0.22);
    ctx.fillStyle = WHITE;
    ctx.font = `500 ${ddSize}px ${FONT}`;
    ctx.fillText(value, x, top + em(0.68) + em(0.35) + ddSize * 0.95);
  };
  field(labels.date, labels.dateValue, left, rowTop);
  field(labels.time, labels.timeValue, colB, rowTop);
  field(labels.place, "Experience House · Rooftop", left, placeTop);
  ctx.fillStyle = "rgba(247, 247, 247, 0.62)";
  ctx.font = `400 ${smallSize}px ${FONT}`;
  ctx.fillText("Rua Gerivatiba, 207 · Butantã · São Paulo", left, gridBottom - smallSize * 0.25);

  ctx.strokeStyle = "rgba(192, 197, 204, 0.36)";
  ctx.lineWidth = 2;
  ctx.setLineDash([9, 7]);
  ctx.beginPath();
  ctx.moveTo(x0 + em(1.2), tearY);
  ctx.lineTo(x0 + CARD_W - em(1.2), tearY);
  ctx.stroke();
  ctx.setLineDash([]);

  // Selo desenhado.
  const sealR = em(1.2) * (10.5 / 12);
  const sealX = left + em(1.2);
  ctx.strokeStyle = ORANGE;
  ctx.lineWidth = em(2.4) * (1.5 / 24);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.arc(sealX, stubMid, sealR, 0, Math.PI * 2);
  ctx.stroke();
  const s = em(2.4) / 24;
  ctx.beginPath();
  ctx.moveTo(sealX + (7.5 - 12) * s, stubMid + (12.5 - 12) * s);
  ctx.lineTo(sealX + (10.5 - 12) * s, stubMid + (15.5 - 12) * s);
  ctx.lineTo(sealX + (16.5 - 12) * s, stubMid + (9 - 12) * s);
  ctx.stroke();

  // "Presença confirmada" + "Esperamos você!" (no lugar da contagem).
  const textX = left + em(2.4) + em(0.9);
  ctx.fillStyle = WHITE;
  ctx.font = `600 ${em(0.74)}px ${FONT}`;
  spaced(ctx, labels.confirmed.toUpperCase(), textX, stubMid - em(0.12), em(0.74) * 0.18);
  ctx.fillStyle = ORANGE;
  ctx.font = `600 ${em(0.72)}px ${FONT}`;
  spaced(ctx, labels.highlight.toUpperCase(), textX, stubMid + em(0.45) + em(0.72) * 0.8, em(0.72) * 0.14);

  // Seta dupla da marca à direita.
  ctx.save();
  const markH = em(2.1);
  const scale = markH / 519;
  ctx.translate(right - 373 * scale, stubMid - markH / 2);
  ctx.scale(scale, scale);
  ctx.translate(-776, -280);
  ctx.fillStyle = ORANGE;
  ARROW_PATHS.forEach((d) => ctx.fill(new Path2D(d)));
  ctx.restore();

  // Assinatura discreta abaixo do cartão.
  const signY = y0 + CARD_H + (H - (y0 + CARD_H)) / 2;
  const ghW = (34 * 420) / 112;
  const remusW = (20 * 237) / 63;
  const total = ghW + 28 + remusW;
  if (gh) {
    ctx.globalAlpha = 0.9;
    ctx.drawImage(gh, (W - total) / 2, signY - 17, ghW, 34);
  }
  if (remus) {
    ctx.globalAlpha = 0.65;
    ctx.drawImage(remus, (W - total) / 2 + ghW + 28, signY - 10, remusW, 20);
  }
  ctx.globalAlpha = 1;

  return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob"))), "image/png"));
}

// Gera (ou reaproveita) a imagem do convite. A tela final chama isto com antecedência, para o clique
// entregar o arquivo na hora (o compartilhamento do celular exige o gesto ainda "fresco").
const cache = new Map();
export function preparePassImage(params) {
  const key = JSON.stringify(params);
  if (!cache.has(key)) {
    const job = drawPass(params).catch((error) => {
      cache.delete(key);
      throw error;
    });
    cache.set(key, job);
  }
  return cache.get(key);
}

function download(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Entrega o arquivo: no celular, compartilhar (salvar na galeria); no computador, download direto
// (lá o compartilhamento abriria a janela "Compartilhar" do sistema em vez de baixar).
export async function deliverPassImage(blob, fileName) {
  const file = TOUCH && typeof File === "function" ? new File([blob], fileName, { type: "image/png" }) : null;
  if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return "shared";
    } catch (error) {
      if (error.name === "AbortError") return "cancelled";
      // Gesto expirado ou compartilhamento indisponível: baixa.
    }
  }
  download(blob, fileName);
  return "downloaded";
}
